import { useState, useEffect } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import gql from 'graphql-tag';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useGetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useSessionData } from '@confluence/session-data';
import { ONBOARDING_SHARE_AND_RESTRICT_DIALOG } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

// eslint-disable-next-line import/no-cycle
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../Controller';

const NewUserQuery = gql`
	query useSpotlightStateNewUserQuery($cloudId: String!, $userId: String!) @experimental {
		experimentalSiteUserPersonalization(cloudId: $cloudId, userId: $userId) {
			attributes {
				name
				value
			}
		}
	}
`;

const CONFLUENCE_FIRST_ACTIVE_TRAIT = 'confluence_first_active';

export function useShouldShowSpotlight() {
	const { isExistingUser } = useIsExistingUser();
	const { isSpotlightActive } = useSpotlightState();

	//Only existing users will see the spotlights
	if (isExistingUser) {
		return { showSpotlight: isSpotlightActive };
	}
	return { showSpotlight: false };
}

export function useSpotlightState() {
	const spotlightKey = ONBOARDING_SHARE_AND_RESTRICT_DIALOG.GENERAL_AND_SPECIFIC_ACCESS_SEEN;
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);
	const { data, loading, error: spotlightError } = useGetOnboardingState(spotlightKey);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (data && !loading && !spotlightError) {
			const spotlightSeen =
				data?.onboardingState?.find((prop) => prop.key === spotlightKey)?.value === 'true';

			if (!spotlightSeen) {
				setIsSpotlightActive(true);
			}
		}
	}, [data, loading, spotlightError, spotlightKey]);

	useEffect(() => {
		if (spotlightError && !loading) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					actionSubject: spotlightKey,
					action: 'spotlight failed',
				},
			}).fire();
		}
	}, [spotlightKey, spotlightError, loading, createAnalyticsEvent]);

	return { isSpotlightActive };
}

// a user is considered existing once it has been more than 7 days
// since they were first active
export const useIsExistingUser = () => {
	const { cloudId, userId } = useSessionData();

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { data, loading, error } = useQuery(NewUserQuery, {
		variables: { cloudId, userId },
		skip: !userId || !cloudId,
	});

	if (loading || error) {
		return {
			isExistingUser: false,
		};
	}

	const attributes = data?.experimentalSiteUserPersonalization?.attributes;

	const firstActiveValue = attributes?.find(
		({ name }: { name: string }) => name === CONFLUENCE_FIRST_ACTIVE_TRAIT,
	)?.value;
	const currentDate = Date.now();

	const firstActiveDate = new Date(firstActiveValue);
	// Check if the date is invalid. If it is, we assume the user is new
	// and return true to avoid mistakenly showing the onboarding message.
	// Related ticket: https://product-fabric.atlassian.net/browse/USD-698
	const isNotValidDate = isNaN(firstActiveDate.getTime()) || isNaN(currentDate);

	if (isNotValidDate) {
		return {
			isExistingUser: false,
		};
	}

	const diffTime = Math.abs(currentDate - firstActiveDate.getTime());
	const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

	return {
		isExistingUser: Boolean(diffDays > 7),
	};
};
